import FVector from '@data/geometry/FVector';
import { SensorData } from 'Simulations/SensorPoint';
import { toRadians } from 'Utils/UtilityFunctions';
import { CONTEXT_KEYS, ContextHelper, StageContext } from '../../Context';
import { StageParameters, TransformStage } from '../../Stage';

export enum ProjectionType {
    NO_PROJECTION = 0,
    HORIZONTAL = 1,
    AVERAGE = 2,
    ORTHOGONAL_PLANE = 3,
}

export interface ProjectParams extends StageParameters {
    projectionType: ProjectionType;
    projectionAngle?: number;
}
class ProjectStage extends TransformStage<ProjectParams> {
    constructor(params: ProjectParams) {
        super(params);
    }

    override process(input: SensorData, context: StageContext): SensorData {
        const direction = input.getVectorialResult();
        const average = ContextHelper.get<FVector>(context, CONTEXT_KEYS.AVERAGE);

        const projectedDirection = this.project(direction, average);
        input.setVectorialValue(projectedDirection);
        input.setScalarValue(projectedDirection.mag());

        return input;
    }

    private project(direction: FVector, average: FVector): FVector {
        switch (this.getParams().projectionType) {
            case 0:
                return direction;
            case 1:
                return this.horizontalProjection(direction);

            case 2:
                return this.average(direction, average);

            case 3:
                return this.orthogonalPlane(direction);
            default:
                throw new Error('Invalid projection type');
        }
    }

    private horizontalProjection(incomingDirection: FVector): FVector {
        return new FVector(incomingDirection.x, incomingDirection.y, 0);
    }

    private average(direction: FVector, movingAverage: FVector): FVector {
        const projectedDirection = FVector.mult(movingAverage, FVector.dot(movingAverage, direction) / movingAverage.magSq());
        return projectedDirection;
    }

    private orthogonalPlane(direction: FVector): FVector {
        const angle = this.getParams().projectionAngle;
        if (angle === undefined) {
            throw new Error('projectionAngle is required when projectionType is ORTHOGONAL_PLANE'); // throw or notify?
        }
        const r = direction.mag();
        let x = direction.x,
            y = direction.y,
            z = direction.z;
        let theta = Math.atan2(y, x);
        const phi = Math.asin(z / r);

        //theta = toRadians(this.parentFrame.parentLayer.status.projections.THETA) - toRadians(90);
        theta = toRadians(angle) - toRadians(90);
        // Projection of vector v on a line of angle alpha:
        // STEP 1: find out the versor generated with angle alpha from a rotation of e1
        // x' = R * e1, R is rotation matrix
        // STEP 2: find out the projection of v on x'
        // v_x' = v*x'/ ||x'||^2 * x'

        // Given theta and phi, generate a vector using spherical coordinates
        // https://www.youmath.it/lezioni/analisi-due/varie/2278-coordinate-sferiche.html
        x = Math.cos(phi) * Math.cos(theta);
        y = Math.cos(phi) * Math.sin(theta);
        z = Math.sin(phi);

        const rotatedVersor = new FVector(x, y, z);
        // Compute the projection
        const projectedDir = FVector.mult(rotatedVersor, FVector.dot(rotatedVersor, direction) / rotatedVersor.magSq()); // Mag or mag sq?? check it

        return projectedDir;
    }
}

export default ProjectStage;
